export const INVALID_DATE_MESSAGE = 'Enter a valid date with a four-digit year (YYYY-MM-DD).';
export const INVALID_DATE_RANGE_MESSAGE = 'End date must be on or after the start date.';

/** Supported year bounds; the backend guard enforces the same window. */
export const MIN_SHIFT_YEAR = 1900;
export const MAX_SHIFT_YEAR = 2100;

/** `min`/`max` for the date inputs, so the native picker steers users too. */
export const MIN_SHIFT_DATE = `${MIN_SHIFT_YEAR}-01-01`;
export const MAX_SHIFT_DATE = `${MAX_SHIFT_YEAR}-12-31`;

export const YEAR_OUT_OF_RANGE_MESSAGE = `Year must be between ${MIN_SHIFT_YEAR} and ${MAX_SHIFT_YEAR}.`;

/** Exactly four year digits — anything wider is rejected outright (SC-6530). */
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True when the value is a real `YYYY-MM-DD` calendar day.
 *
 * Chrome's <input type="date"> lets a user type a year of up to six digits and
 * reports it verbatim through `value`, and `new Date('275760-01-01')` is a *valid*
 * JS Date — so a shift was assigned with year 275760 (SC-6530). Validate the
 * string shape rather than trusting the Date constructor, then confirm the parts
 * describe a day that exists so 2026-02-30 cannot slip past either.
 *
 * Year bounds are deliberately left to {@link getShiftDateRangeError} so it can
 * tell an out-of-range year apart from a malformed one.
 */
export function isValidShiftDate(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;

  const match = DATE_PATTERN.exec(value.trim());
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;

  // Day 0 of the next month is the last day of this one — the leap-year-aware
  // upper bound for `day`.
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= daysInMonth;
}

/**
 * The inline message for a single date field, or null when it is empty or usable.
 *
 * An empty value is not an error here — a half-filled form is the job of the
 * existing required-field checks. A *filled* field is judged on its own, though,
 * so a bad start date is reported the moment it is entered rather than waiting for
 * the end date to be supplied too.
 */
export function getShiftDateError(value: string | null | undefined): string | null {
  if (!value) return null;

  if (!isValidShiftDate(value)) return INVALID_DATE_MESSAGE;

  const year = Number(value.trim().slice(0, 4));
  if (year < MIN_SHIFT_YEAR || year > MAX_SHIFT_YEAR) {
    return YEAR_OUT_OF_RANGE_MESSAGE;
  }

  return null;
}

/**
 * The inline message for an assignment's date pair, or null when it is usable.
 *
 * Each date is judged on its own first, so the message appears as soon as one
 * field is wrong; ordering is only checked once both are present and valid.
 */
export function getShiftDateRangeError(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null {
  const startError = getShiftDateError(startDate);
  if (startError) return startError;

  const endError = getShiftDateError(endDate);
  if (endError) return endError;

  // Ordering needs both halves; until then there is nothing to compare.
  if (!startDate || !endDate) return null;

  // Both are known-good `YYYY-MM-DD`, so a lexicographic compare is also a
  // chronological one.
  return startDate.trim() > endDate.trim() ? INVALID_DATE_RANGE_MESSAGE : null;
}
